import { useEffect } from 'react';
import { Outlet } from 'react-router';
import { useRealtime } from '@/lib/realtime';
import { cn } from '@/lib/utils';
import { CommandPalette } from './command-palette';
import { GlobalDialogs } from './global-dialogs';
import { MobileNav } from './mobile-nav';
import { SidebarContent } from './sidebar';
import { useSidebar } from './sidebar-store';
import { Topbar } from './topbar';

function isTyping(el: EventTarget | null) {
  return (
    el instanceof HTMLElement &&
    (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName))
  );
}

/**
 * Estrutura: sidebar sobre o fundo + "palco" arredondado com o conteúdo (refs 01, 02, 06).
 * Sidebar: expandida (240px), compacta (72px) ou oculta (aparece flutuando ao encostar na borda).
 */
export function AppShell() {
  const { mode, peek, setPeek, cycle } = useSidebar();
  useRealtime();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === '[' && !e.metaKey && !e.ctrlKey && !e.altKey && !isTyping(e.target)) cycle();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cycle]);

  return (
    <div className="flex min-h-dvh bg-canvas">
      <a
        href="#conteudo"
        className="sr-only z-50 rounded-control bg-signal px-3 py-2 text-signal-fg focus:not-sr-only focus:fixed focus:top-3 focus:left-3"
      >
        Pular para o conteúdo
      </a>

      <aside
        className={cn(
          'sticky top-0 hidden h-dvh shrink-0 transition-[width] duration-300 ease-[var(--ease-out-expo)] md:block',
          mode === 'expanded' && 'w-[240px]',
          mode === 'compact' && 'w-[72px]',
          mode === 'hidden' && 'w-0',
        )}
      >
        {mode !== 'hidden' && <SidebarContent compact={mode === 'compact'} />}
      </aside>

      {mode === 'hidden' && (
        <>
          <div
            className="fixed inset-y-0 left-0 z-40 hidden w-2 md:block"
            onMouseEnter={() => setPeek(true)}
            aria-hidden
          />
          <aside
            onMouseLeave={() => setPeek(false)}
            className={cn(
              'fixed inset-y-2 left-2 z-40 hidden w-[240px] rounded-stage bg-canvas shadow-lift transition-transform duration-300 ease-[var(--ease-out-expo)] md:block',
              peek ? 'translate-x-0' : '-translate-x-[calc(100%+16px)]',
            )}
          >
            <SidebarContent />
          </aside>
        </>
      )}

      <div className={cn('flex min-w-0 flex-1 flex-col md:py-2 md:pr-2', mode === 'hidden' && 'md:pl-2')}>
        <main
          id="conteudo"
          className="relative flex min-h-dvh flex-1 flex-col bg-stage pb-20 md:min-h-0 md:rounded-stage md:pb-0 md:shadow-[inset_0_0_0_1px_var(--color-line)]"
        >
          <Topbar />
          <div className="flex-1">
            <Outlet />
          </div>
        </main>
      </div>

      <MobileNav />
      <CommandPalette />
      <GlobalDialogs />
    </div>
  );
}
